import { useState, type FormEvent } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "@/hooks/use-page-title";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Building2, Loader2, CheckCircle } from "lucide-react";

export function InviteSignupPage() {
  usePageTitle("Accept Invitation");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") ?? "";
  const email = searchParams.get("email") ?? "";
  const org = searchParams.get("org") ?? "";
  const role = searchParams.get("role") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const { error: signUpError } = await authClient.signUp.email({
        name: email.split("@")[0],
        email,
        password,
      });

      if (signUpError) {
        setError(signUpError.message ?? "Failed to create account");
        setLoading(false);
        return;
      }

      const { error: acceptError } = await authClient.organization.acceptInvitation({
        invitationId: token,
      });

      if (acceptError) {
        setError(acceptError.message ?? "Failed to accept invitation");
        setLoading(false);
        return;
      }

      const { data: orgs } = await authClient.organization.list();
      const joinedOrg = orgs?.find((o) => o.name === org);
      setAccepted(true);
      const target = joinedOrg ? `/${joinedOrg.id}/${joinedOrg.slug}/dashboard` : "/onboarding/select-org";
      setTimeout(() => navigate(target, { replace: true }), 1500);
    } catch {
      setError("Unable to connect. Please try again.");
      setLoading(false);
    }
  }

  if (accepted) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>You're in!</CardTitle>
          <CardDescription>Redirecting you to the dashboard...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-4">
          <CheckCircle className="h-8 w-8 text-green-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create your account</CardTitle>
        <CardDescription>
          Sign up to join the organization
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3 rounded-lg border p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">{org}</p>
            <p className="text-sm text-muted-foreground">Role: {role}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              readOnly
              className="bg-muted"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign up & join
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
